import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaCalendarAlt, FaUser, FaTag, FaSearch, FaFilter } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

/**
 * Blogs page component
 */
const Blogs = () => {
  // State for blog data and filtering
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  // Categories for filtering
  const categories = [
    'all',
    'teaching strategies',
    'educational technology',
    'classroom management',
    'student engagement',
    'assessment',
    'professional development'
  ];

  // Server-provided blog data
  const [blogsData, setBlogsData] = useState([]);

  // Format date to display in a user-friendly format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Load blog data
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await api.get('/blogs');
        const data = response?.data?.data || response?.data?.blogs || response?.data || [];
        const list = Array.isArray(data) ? data : [];
        setBlogsData(list);
        const featured = list.find((blog) => blog.featured);
        setFeaturedBlog(featured || list[0] || null);
        setBlogs(list);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Filter blogs based on user selections
  const filteredBlogs = blogs
    .filter(blog => {
      // Filter by category
      if (categoryFilter === 'all') return true;
      return blog.category === categoryFilter;
    })
    .filter(blog => {
      // Filter by search term
      return blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
             blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
             blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
             blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    });

  // Paginate blogs
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <>
      <Helmet>
        <title>Meira Blog | Educational Resources & Insights</title>
        <meta name="description" content="Explore educational articles, teaching strategies, EdTech insights, and professional development resources for educators." />
      </Helmet>

      {/* Hero section */}
      <div className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">Meira Blog</h1>
            <p className="mt-5 max-w-xl mx-auto text-xl text-indigo-100">
              Insights, resources, and strategies for modern education
            </p>
          </div>
        </div>
      </div>

      {/* Featured blog post */}
      {featuredBlog && !loading && (
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 mb-8">Featured Article</h2>
            <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
              <div className="relative lg:col-start-2">
                <div className="aspect-w-5 aspect-h-3 rounded-lg shadow-lg overflow-hidden">
                  <img 
                    src={featuredBlog.image} 
                    alt={featuredBlog.title} 
                    className="object-cover object-center"
                  />
                </div>
              </div>
              <div className="mt-8 lg:mt-0 lg:col-start-1 lg:row-start-1">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <FaCalendarAlt className="mr-2 h-4 w-4 text-gray-400" />
                  <span>{formatDate(featuredBlog.date)}</span>
                  <span className="mx-2">·</span>
                  <FaUser className="mr-2 h-4 w-4 text-gray-400" />
                  <span>{featuredBlog.author}</span>
                  <span className="mx-2">·</span>
                  <span>{featuredBlog.readTime}</span>
                </div>
                <Link to={`/blog/${featuredBlog.id}`} className="block">
                  <h3 className="text-2xl font-bold text-gray-900">{featuredBlog.title}</h3>
                </Link>
                <p className="mt-3 text-lg text-gray-500">{featuredBlog.excerpt}</p>
                <div className="mt-4">
                  <Link
                    to={`/blog/${featuredBlog.id}`}
                    className="text-primary hover:text-primary-dark font-medium"
                  >
                    Read more →
                  </Link>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {featuredBlog.tags.map((tag, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-primary-light text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter and search section */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4 items-center justify-between">
            <div className="flex space-x-2 items-center">
              <FaFilter className="text-gray-400" />
              <span className="text-gray-700">Filter by:</span>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
                className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
              >
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-primary focus:ring-1 focus:ring-primary sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Blog posts listing */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 mb-12">
            {categoryFilter === 'all' ? 'All Articles' : `${categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1)} Articles`}
          </h2>
          
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : currentBlogs.length > 0 ? (
            <>
              <div className="grid gap-16 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-12">
                {currentBlogs.map((blog) => (
                  <div key={blog.id} className="blog-card">
                    <div className="mb-4 overflow-hidden rounded-lg">
                      <img 
                        src={blog.image} 
                        alt={blog.title} 
                        className="w-full h-48 object-cover transform transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <FaCalendarAlt className="mr-2 h-4 w-4 text-gray-400" />
                      <span>{formatDate(blog.date)}</span>
                      <span className="mx-2">·</span>
                      <FaUser className="mr-2 h-4 w-4 text-gray-400" />
                      <span>{blog.author}</span>
                      <span className="mx-2">·</span>
                      <span>{blog.readTime}</span>
                    </div>
                    <Link to={`/blog/${blog.id}`} className="block">
                      <h3 className="mt-2 text-xl font-semibold text-gray-900">{blog.title}</h3>
                    </Link>
                    <p className="mt-3 text-base text-gray-500">{blog.excerpt}</p>
                    <div className="mt-4">
                      <Link
                        to={`/blog/${blog.id}`}
                        className="text-primary hover:text-primary-dark font-medium"
                      >
                        Read more →
                      </Link>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <span 
                          key={index} 
                          className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="mt-12 flex justify-center">
                  <ul className="flex items-center">
                    <li>
                      <button
                        onClick={() => paginate(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                          currentPage === 1 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => (
                      <li key={index}>
                        <button
                          onClick={() => paginate(index + 1)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                            currentPage === index + 1
                              ? 'z-10 bg-primary text-white'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li>
                      <button
                        onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                          currentPage === totalPages 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No articles found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setCategoryFilter('all');
                    setSearchTerm('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Newsletter signup */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Subscribe to Our Newsletter
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Get the latest articles, resources, and teaching strategies delivered to your inbox.
            </p>
          </div>
          <form className="mt-8 sm:flex sm:justify-center">
            <div className="min-w-0 flex-1">
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <div className="mt-3 sm:mt-0 sm:ml-3">
              <button
                type="submit"
                className="block w-full rounded-md border border-transparent px-4 py-3 bg-primary text-base font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:px-8"
              >
                Subscribe
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Blogs;
